import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useJourneys } from '../lib/useJourneys';
import { useReveal } from '../lib/useReveal';

export default function Home() {
  const wrapRef = useReveal();
  const videoRef = useRef(null);
  const journeys = useJourneys();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <div ref={wrapRef} data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section data-testid="home-hero" className="relative h-[100svh] min-h-[760px] w-full overflow-hidden bg-[color:var(--tdt-ink)]">
        <video
          ref={videoRef}
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover"
          src="/assets/video/hero-loop.mp4"
          poster="/assets/photos/hero-vineyard.jpg"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="hero-veil" />
        <div className="relative z-10 h-full flex flex-col">
          {/* spacer reserves space for the fixed header so hero text never overlaps the logo */}
          <div className="shrink-0 h-[140px] sm:h-[170px] lg:h-[210px]" aria-hidden />
          <div className="flex-1 flex flex-col justify-end">
            <div className="max-w-[1400px] mx-auto w-full px-5 sm:px-6 lg:px-12 pb-16 sm:pb-20 lg:pb-28 text-[color:var(--tdt-ivory)]">
              <div className="reveal max-w-3xl">
                <div className="tdt-eyebrow tdt-eyebrow-light mb-5 sm:mb-6">Tour de Taste <span className="text-[color:var(--tdt-cream)]/80 normal-case tracking-normal italic font-light">(Melbourne only for now)</span></div>
                <h1 className="font-serif font-light tracking-tight leading-[1.04] text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-5 sm:mb-6">
                  Private culinary journeys,<br />
                  <em className="not-italic text-[color:var(--tdt-gold-soft)]">exquisitely curated.</em>
                </h1>
                <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-xl text-[color:var(--tdt-cream)] font-light mb-8 sm:mb-10">
                  High-end food and wine experiences for discerning guests seeking rare access, seamless hospitality, and unforgettable taste.
                </p>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
                  <Link to="/enquire" data-testid="hero-enquire-btn" className="btn-pill btn-gold w-full sm:w-auto justify-center">
                    Enquire Privately <ArrowRight size={14} />
                  </Link>
                  <Link to="/signature-journeys" data-testid="hero-journeys-btn" className="btn-pill btn-outline-ivory w-full sm:w-auto justify-center">
                    Explore Signature Journeys
                  </Link>
                </div>
              </div>
            </div>
          </div>
          {/* scroll cue */}
          <div className="pb-6 flex justify-center text-[color:var(--tdt-cream)]/70">
            <div className="flex flex-col items-center gap-2 text-[10px] tracking-[0.4em] uppercase">
              <span>Scroll</span>
              <span className="block w-px h-10 bg-[color:var(--tdt-gold-soft)]" />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- INTRO ---------- */}
      <section data-testid="home-intro" className="bg-[color:var(--tdt-ivory)] py-16 sm:py-20 lg:py-36">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <div className="tdt-eyebrow mb-5">A more refined way</div>
            <h2 className="font-serif font-light text-3xl sm:text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[color:var(--tdt-ink)]">
              To experience food and wine.
            </h2>
            <div className="hr-gold mt-8" />
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="font-serif text-2xl leading-snug text-[color:var(--tdt-ink)] mb-8">
              Tour de Taste is for those who want more than a reservation.
            </p>
            <p className="text-[15px] sm:text-base lg:text-base leading-relaxed text-[color:var(--tdt-muted)] mb-6">
              We create privately hosted culinary experiences that reveal the people, places, and stories behind exceptional food and wine. Every journey is shaped around the guest — their tastes, occasion, pace, and desired level of indulgence.
            </p>
            <p className="text-[15px] sm:text-base lg:text-base leading-relaxed text-[color:var(--tdt-muted)]">
              Whether you are celebrating an anniversary, entertaining clients, welcoming international guests, or seeking an unforgettable day of discovery, Tour de Taste offers a seamless experience from first enquiry to final glass.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- EDITORIAL DUO ---------- */}
      <section data-testid="home-editorial" className="bg-[color:var(--tdt-cream)] py-16 sm:py-14 sm:py-18 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="media-card reveal relative aspect-[4/5]">
            <img src="/assets/photos/chefs-table-oysters.jpg" alt="Chef's table with oysters and champagne" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="reveal lg:pl-10">
            <div className="tdt-eyebrow mb-5">The signature experience</div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.05] mb-6 text-[color:var(--tdt-ink)]">
              The Private Table Journey
            </h2>
            <p className="text-[15px] sm:text-base lg:text-base leading-relaxed text-[color:var(--tdt-muted)] mb-4">
              A seamless, privately hosted evening or day experience curated around the guest’s tastes — a premium aperitif, behind-the-scenes tasting, chef’s table dining, sommelier pairing, and a final indulgence.
            </p>
            <p className="text-[15px] sm:text-base lg:text-base leading-relaxed text-[color:var(--tdt-muted)] mb-10">
              Rather than a fixed itinerary, the evening is designed around the occasion. A handwritten menu, flowers, anniversary card, or a chauffeured transfer can complete the moment.
            </p>
            <Link to="/signature-journeys" className="btn-pill btn-outline-ink" data-testid="editorial-cta">
              Discover the journey
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- SIGNATURE JOURNEYS ---------- */}
      <section data-testid="home-journeys" className="bg-[color:var(--tdt-ivory)] py-16 sm:py-20 lg:py-36">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6 reveal">
            <div>
              <div className="tdt-eyebrow mb-5">Our Signature Journeys</div>
              <h2 className="font-serif font-light text-3xl sm:text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[color:var(--tdt-ink)] max-w-2xl">
                Five distinct ways to be hosted.
              </h2>
            </div>
            <Link to="/signature-journeys" className="text-sm uppercase tracking-[0.22em] text-[color:var(--tdt-ink)] hover:text-[color:var(--tdt-gold)] transition-colors inline-flex items-center gap-2" data-testid="all-journeys-link">
              View all <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {journeys.map((j, idx) => (
              <Link
                to={`/signature-journeys#${j.slug}`}
                key={j.slug}
                data-testid={`journey-card-${j.slug}`}
                className="reveal group block"
              >
                <div className="media-card relative aspect-[4/5] mb-6">
                  <img src={j.image} alt={j.name} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute top-4 left-4 z-10 text-[color:var(--tdt-ivory)] text-xs tracking-[0.3em]">{j.number}</div>
                </div>
                <div className="tdt-eyebrow mb-3">Investment {j.investment}</div>
                <h3 className="font-serif text-2xl lg:text-3xl leading-snug text-[color:var(--tdt-ink)] mb-3 group-hover:text-[color:var(--tdt-burgundy)] transition-colors">
                  {j.name}
                </h3>
                <p className="text-sm leading-relaxed text-[color:var(--tdt-muted)]">{j.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHY ---------- */}
      <section data-testid="home-why" className="relative bg-[color:var(--tdt-ink)] text-[color:var(--tdt-ivory)] py-16 sm:py-20 lg:py-36 overflow-hidden">
        <img src="/assets/photos/private-dining.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[color:var(--tdt-ink)]/70" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12">
          <div className="max-w-2xl reveal mb-16">
            <div className="tdt-eyebrow tdt-eyebrow-light mb-5">Why Tour de Taste</div>
            <h2 className="font-serif font-light text-3xl sm:text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">Designed for discerning guests.</h2>
            <p className="text-[color:var(--tdt-cream)] leading-relaxed">
              Every Tour de Taste experience is considered, personal, and beautifully paced. We curate the venues, hosts, producers, transport, timings, dietary details, and finishing touches so guests can simply arrive and enjoy.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
            {[
              ['Private access', 'Chef’s tables, cellar rooms, private dining spaces, artisan makers, and hidden venues.'],
              ['Seamless hospitality', 'From enquiry to final course, every detail is thoughtfully managed.'],
              ['Tailored curation', 'Each experience is shaped around your tastes, occasion, and style.'],
              ['Elevated local knowledge', 'Discover the destination through those who define its food and wine culture.'],
            ].map(([title, body], i) => (
              <div key={title} className="reveal" data-testid={`why-${i}`}>
                <div className="hr-gold mb-5" />
                <h3 className="font-serif text-2xl leading-snug mb-3">{title}</h3>
                <p className="text-sm leading-relaxed text-[color:var(--tdt-cream)]/85">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section data-testid="home-cta" className="bg-[color:var(--tdt-ivory)] py-20 sm:py-24 lg:py-36">
        <div className="max-w-[900px] mx-auto px-5 sm:px-6 lg:px-12 text-center reveal">
          <div className="tdt-eyebrow mb-6">Begin Your Private Culinary Journey</div>
          <h2 className="font-serif font-light text-3xl sm:text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[color:var(--tdt-ink)] mb-7">
            Tell us about your occasion.
          </h2>
          <p className="text-[15px] sm:text-base lg:text-base leading-relaxed text-[color:var(--tdt-muted)] max-w-xl mx-auto mb-10">
            Share your guests, preferences, and the feeling you wish to create. We will curate an experience worthy of the moment.
          </p>
          <Link to="/enquire" data-testid="cta-enquire-btn" className="btn-pill btn-gold">
            Enquire Privately <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
